/**
 * Props for the DOB-Modal component.
 *
 * @prop {Object} config - Configuration object for the modal.
 * @prop {Boolean} config.enabled - Determines if the modal is visible. Default is true.
 * @prop {String} [config.width] - Width of the modal. Default is 'auto'.
 * @prop {Boolean} [config.persistent] - If true, the modal cannot be dismissed by clicking outside.
 * @prop {String} [config.title] - Title text for the modal.
 * @prop {String} [config.placeholder] - Placeholder text for the title.
 * @prop {String} [config.subtitle] - Subtitle text for the modal.
 * @prop {String} [config.body] - Body text for the modal.
 * @prop {Array} [config.dataDetails] - Array of data details to be displayed.
 * @prop {Object} [config.dataListTranslation] - Translation object for data details.
 * @prop {String} [config.values] - Additional values to be displayed.
 * @prop {String} [config.error] - Error message to be displayed.
 * @prop {String} [config.btn1Label] - Label for the first button.
 * @prop {String} [config.btn2Label] - Label for the second button.
 * @prop {String} [config.btn3Label] - Label for the third button.
 * @prop {String} [config.btn1Color] - Color for the first button. Default is primaryColor.
 * @prop {String} [config.btn2Color] - Color for the second button. Default is primaryColor.
 * @prop {String} [config.btn3Color] - Color for the third button. Default is primaryColor.
 * @prop {Boolean} [config.enableSave] - If true, the second button is disabled.
 * @prop {Boolean} [config.enableCancel] - If true, the first button is disabled.
 * @prop {Boolean} [config.close] - If true, a close button is displayed.
 * @prop {Function} [config.saveCallback] - Callback function for the second button action.
 * @prop {Function} [config.cancelCallback] - Callback function for the first button action.
 * @prop {Boolean} [config.persist] - If true, the modal remains open after the second button action.
 * @prop {Boolean} [config.persistOnBtn1] - If true, the modal remains open after the first button action.
 */
<template>
  <v-dialog
    v-model="config.enabled"
    :width="config.width || 'auto'"
    :persistent="config.persistent || true"
    data-qa="modal-dialog-aag46j">
    <v-card data-qa="modal-card-dwrz5d">
      <v-container data-qa="modal-container-5ifrea">
        <v-card-text data-qa="modal-card-text-p0at6g">
          <v-row data-qa="modal-row-7l711">
            <v-col v-if="(config.title || config.placeholder)" cols="12" data-qa="modal-col-pbnvef">
              <p v-if="(config.title || config.placeholder)" class="pl-2 text-h5 font-weight-bold black--text" data-qa="modal-p-n5xu3">{{ (config.placeholder) ? $t(config.placeholder): config.title }} </p>
            </v-col>
            <v-col cols="12" v-if="config.subtitle || config.body" data-qa="modal-col-hc2cq6">
              <p v-if="config.subtitle" class="mx-2 my-0" data-qa="modal-p-4ofvmd">{{ config.subtitle }}</p>
              <p v-if="config.body" class="mx-2 my-0" data-qa="modal-p-qk8e5p">{{ config.body }}</p>
            </v-col>
            <v-col cols="12" v-if="config.dataDetails" data-qa="modal-col-veka56">
              <p class="mx-4 my-0 pa-2" data-qa="modal-p-fcvfa">
                <DisplayDetails
                  :config="{
                    title: '',
                    dataList: config.dataDetails,
                    translation: config.dataListTranslation || null,
                    screenType: 'confirmScreen'
                  }"
                />
              </p>
            </v-col>
            <v-col v-if="$slots.modalHeaderContent" cols="12">
              <slot name="modalHeaderContent" />
            </v-col>
            <v-col
              cols="12"
              v-if="$slots.modalContent"
              :style="(config.title || config.placeholder) ? 'padding-top: 0px' : 'padding-top: 30px'"
            >
              <p
                v-if="$slots.modalContent"
                class="mx-2 my-0"
                data-qa="modal-p-tatf35"><slot
                name="modalContent"
                data-qa="modal-slot-x83nox"></slot></p
              >
            </v-col>
            <v-col cols="12" v-if="config.values || config.error" data-qa="modal-col-uvvvnp">
              <p v-if="config.values" class="mx-2 my-0" data-qa="modal-p-2hf9k2">{{ config.values }}</p>
              <p v-if="config.error" class="mx-2 my-0" data-qa="modal-p-u0wvz">{{ config.error }}</p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions v-if="(config.btn1Label || config.btn2Label || config.btn3Label)" data-qa="modal-card-actions-olspgd">
          <v-spacer data-qa="modal-spacer-c3rmgu"  />
          <v-btn
            v-if="Boolean(config.btn2Label)"
            @click="onbtn2Action"
            :disabled="config.enableSave"
            class="py-2 px-4 text-capitalize"
            :color="config.btn2Color || primaryColor"
            outlined
            data-qa="modal-btn-wrfzf">{{config.btn2Label}}
          </v-btn>
          <v-btn
            v-if="Boolean(config.btn1Label)"
            @click="onbtn1Action"
            class="py-2 px-4 text-capitalize"
            :disabled="config.enableCancel"
            :color="config.btn1Color || primaryColor"
            :dark="config.enableCancel ? false : true"
            data-qa="modal-btn-1j7zd7">{{config.btn1Label}}
          </v-btn>
          <v-btn
            v-if="Boolean(config.btn3Label)"
            @click="onbtn3Action"
            class="py-2 px-4 text-capitalize"
            :color="config.btn3Color || primaryColor"
            :dark="config.enableBtn3 ? false : true"
          >
            {{config.btn3Label}}
          </v-btn>
        </v-card-actions>
        <v-btn v-if="config.close" class="close-btn" icon @click="onCancel" data-qa="modal-btn-8acytc">
          <v-icon color="black" size="24" data-qa="modal-icon-m0porp">mdi-close</v-icon>
        </v-btn>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import DisplayDetails from '@/views/common/DisplayDetails.vue'

export default {
  name: 'DOB-Modal',
  props: {
    config: {
      type: Object,
      default: undefined,
      enabled: true
    }
  },
  components: {
    DisplayDetails
  },
  methods: {
    onbtn2Action () {
      this.$emit('btn2Action', true)
      if (this.config.saveCallback) {
        this.config.saveCallback()
      }
      if (this.config.btn2Callback) {
        this.config.btn2Callback()
      }
      if (this.config.persist) {
        this.config.enabled = true
      } else {
        this.config.enabled = false
      }
    },
    onbtn1Action () {
      this.$emit('btn1Action', true)
      if (this.config.cancelCallback) {
        this.config.cancelCallback()
      }
      if (this.config.btn1Callback) {
        this.config.btn1Callback()
      }
      if (this.config?.persistOnBtn1) {
        this.config.enabled = true
      } else {
        this.config.enabled = false
      }
    },
    onbtn3Action () {
      this.$emit('btn3Action', true)
      if (this.config.cancelCallback) {
        this.config.cancelCallback()
      }
      if (this.config.btn3Callback) {
        this.config.btn3Callback()
      }
    },
    onCancel () {
      this.$emit('cancel', true)
      if (this.config.cancelCallback) {
        this.config.cancelCallback()
      }
      this.config.enabled = false
    }
  }
}
</script>

<style scoped>
  .close-btn {
    margin-top: 8px;
    margin-right: 8px;
    position: absolute;
    top: 0;
    right: 0;
    color: #000000;
  }
</style>
